import React, { useEffect, useState, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { DatePicker, TextField, SelectField, Switch } from '../Base/Form'
import SelectLocation from './SelectLocation'
import PromoFields from './PromoFields'
import useResponsiveView from '../../hooks/useResponsiveView'
import { CircularProgress, Modal } from '@mui/material'
import { getData, postData } from '../../../js/helpers/apiHelper'
import Alert from '../Base/Alert'
import LoadingResultsModal from '../Modals/LoadingResultsModal'
import generateTimeOptions from '../../../js/helpers/generateTimeOptions'
import { setFilters, setStep } from '../../../js/lib/redux/slices/booking'
import constants from '../../../js/lib/constants'

const now = new Date()
const oneYearLater = new Date()
const OneYearAndMonthLater = new Date()
oneYearLater.setFullYear(now.getFullYear() + 1)
OneYearAndMonthLater.setFullYear(now.getFullYear() + 1)
OneYearAndMonthLater.setMonth(OneYearAndMonthLater.getMonth() + 1)

const BookingWidget = ({ data, handleCancel, editMode }) => {
  const {
    dictionary,
    logos,
    driverAgeOptions,
    promoCodeTypeOptions,
    config,
    currentValues,
    waitForResultsLogos,
  } = data
  const timeOptions = generateTimeOptions()
  const [userId, setUserId] = useState('')

  const diffLocationInitalCondition =
    currentValues?.pickUpLocation?.code !==
      currentValues?.dropOffLocation?.code ||
    currentValues?.pickUpLocation?.country !==
      currentValues?.dropOffLocation?.country ||
    currentValues?.pickUpLocation?.text !== currentValues?.dropOffLocation?.text
  const [dropOffAtDiffLocation, setDropOffAtDiffLocation] = useState(
    diffLocationInitalCondition
  )
  const [showDiscount, setShowDiscount] = useState(currentValues?.promos)
  const [isSameDay, setIsSameDay] = useState(false)
  const [isExpanded, setIsExpanded] = useState(editMode || currentValues)
  const { isMobile, isTablet } = useResponsiveView()
  const [openFormModal, setOpenFormModal] = useState(false)
  const [alertMessages, setAlertMessages] = useState([])
  const [loading, setLoading] = useState(false)

  const pickUpDateRef = useRef(null)
  const pickUpTimeRef = useRef(null)
  const dropOffDateRef = useRef(null)
  const dropOffTimeRef = useRef(null)

  const { validation } = dictionary
  const schema = z.object({
    pickUpLocation: z.object(
      {
        text: z.string(),
        value: z.string(),
        country: z.string(),
      },
      { invalid_type_error: validation?.required }
    ),
    dropOffLocation: z
      .object(
        {
          text: z.string(),
          value: z.string(),
          country: z.string(),
        },
        { invalid_type_error: validation?.required }
      )
      .optional(),
    pickUpDate: z.string(),
    pickUpTime: z.string(),
    dropOffDate: z.string(),
    dropOffTime: z.string(),
    // driverAge: z.string().optional(),
    aegeanCode: z
      .union([z.string().length(6, validation?.aegeanCode), z.literal('')])
      .optional(),
    // promos: z
    //   .array(
    //     z.object({
    //       type: z.string(),
    //       code: z.string(),
    //     })
    //   )
    //   .optional(),
    promoType: z.string(),
    promoCode: z.string(),
    userId: z.string().optional(),
  })

  const {
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
    mode: 'onTouched',
    defaultValues: currentValues
      ? {
          ...currentValues,
          pickUpLocation: currentValues.pickUpLocation
            ? {
                text: currentValues.pickUpLocation.text,
                value: currentValues.pickUpLocation.code,
                country: currentValues.pickUpLocation.country,
              }
            : '',
          dropOffLocation: currentValues.dropOffLocation
            ? {
                text: currentValues.dropOffLocation.text,
                value: currentValues.dropOffLocation.code,
                country: currentValues.dropOffLocation.country,
              }
            : '',
        }
      : {},
  })

  const onSubmit = (data) => {
    // console.log('Form data:', data)
    const body = {
      ...data,
      pickUpLocation: {
        code: data.pickUpLocation.value,
        country: data.pickUpLocation.country,
        text: data.pickUpLocation.text,
      },
      dropOffLocation: {
        code: dropOffAtDiffLocation
          ? data.dropOffLocation.value
          : data.pickUpLocation.value,
        country: dropOffAtDiffLocation
          ? data.dropOffLocation.country
          : data.pickUpLocation.country,
        text: dropOffAtDiffLocation
          ? data.dropOffLocation.text
          : data.pickUpLocation.text,
      },
    }
    // setAlertMessages([])
    // setLoading(true)
    // postData(config?.endpoints?.findCar, body)
    //   .then((response) => {
    //     if (response.hasError) {
    //       handleErrors(response.errorMessages)
    //     } else {
    //       //set step to results and reset filters
    //       dispatch(setStep(constants.booking.pageNumber.vehicles))
    //       dispatch(setFilters({}))

    //       window.location.href = response.data.redirectUrl
    //     }
    //   })
    //   .catch(() => handleErrors([dictionary.defaultErrorMessage]))
    //   .finally(() => setLoading(false))
  }

  const handleErrors = (messages) => {
    setAlertMessages(messages)
    document
      .querySelector('.s-booking-widget__form')
      .scrollTo({ behavior: 'smooth', top: 0 })
  }

  useEffect(() => {
    if (watch('pickUpDate') && watch('dropOffDate')) {
      const pickUp = new Date(watch('pickUpDate')).getTime()
      const dropOff = new Date(watch('dropOffDate')).getTime()
      if (pickUp > dropOff) {
        setValue('dropOffDate', '')
      } else if (pickUp === dropOff) {
        setIsSameDay(true)
      } else {
        setIsSameDay(false)
      }
    }
  }, [watch('pickUpDate'), watch('dropOffDate')])

  useEffect(() => {
    if (watch('pickUpDate') && !watch('pickUpTime')) {
      setValue('pickUpTime', '10:00', { shouldValidate: true })
    }
  }, [watch('pickUpDate'), watch('pickUpTime')])

  useEffect(() => {
    if (watch('dropOffDate')) {
      const currentDropOffTime = watch('dropOffTime')

      // Check if same day and pickup time is set
      if (isSameDay && watch('pickUpTime')) {
        const pickUpTimeIndex = timeOptions.indexOf(watch('pickUpTime'))
        const availableTimes = timeOptions.filter(
          (_, index) => index > pickUpTimeIndex
        )

        // Check if current dropoff time is invalid (not in available times or not set)
        const isInvalidTime =
          !currentDropOffTime || !availableTimes.includes(currentDropOffTime)

        if (isInvalidTime && availableTimes.length > 0) {
          // Use first available time after pickup
          setValue('dropOffTime', availableTimes[0], { shouldValidate: true })
        }
      } else if (!currentDropOffTime) {
        // Different days and no time set - use default
        setValue('dropOffTime', '10:00', { shouldValidate: true })
      }
    }
  }, [
    watch('dropOffDate'),
    watch('dropOffTime'),
    isSameDay,
    watch('pickUpTime'),
  ])

  useEffect(() => {
    const handleLoginSuccess = (event) => {
      setUserId(event.detail.isLoggedIn ? event.detail.userId : '')
    }
    window.addEventListener('loggedIn', handleLoginSuccess)

    return () => {
      window.removeEventListener('loggedIn', handleLoginSuccess)
    }
  }, [])

  useEffect(() => {
    if (userId) {
      setValue('userId', userId)
    }
  }, [userId])

  const handlePickUpDateChange = (selectedDate) => {
    if (!selectedDate) {
      pickUpTimeRef.current?.open()
      return
    }

    const nextDay = new Date(selectedDate)
    nextDay.setDate(nextDay.getDate() + 1)
    const currentDropOffDate = watch('dropOffDate')

    // Only update drop-off date if it's not set or is earlier than next day
    const shouldUpdateDropOff =
      !currentDropOffDate ||
      new Date(currentDropOffDate).getTime() < nextDay.getTime()

    if (shouldUpdateDropOff) {
      setValue('dropOffDate', nextDay.toISOString().split('T')[0], {
        shouldValidate: true,
      })
    }

    pickUpTimeRef.current?.open()
  }

  const handleLogin = (e) => {
    e.preventDefault()
    window.dispatchEvent(new CustomEvent('openLoginModal'))
  }

  return (
    <div className="page-container">
      <div className="s-inline-widget">
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="s-inline-widget__form">
          {alertMessages?.length > 0 && <Alert messages={alertMessages} />}
          <div className="s-inline-widget__header">
            <Switch
              name={'dropOffSwitch'}
              label={dictionary.locationSwitch}
              checked={dropOffAtDiffLocation}
              onChange={setDropOffAtDiffLocation}
            />
            <div className="s-inline-widget__logos">
              {logos?.map((logo) => (
                <img key={logo.name} src={logo.url} alt={logo.alt} />
              ))}
            </div>
          </div>
          <div className="row">
            <div
              className={`${dropOffAtDiffLocation ? 'col-lg-6' : 'col-lg-12'}`}>
              <SelectLocation
                name={'pickUpLocation'}
                label={
                  dropOffAtDiffLocation
                    ? dictionary.pickUpLabel
                    : dictionary.pickUpDropOffLabel
                }
                control={control}
                setValue={setValue}
                watch={watch}
                error={errors.pickUpLocation}
                handleFocus={(isFocused) => {
                  if (isFocused && !isExpanded) setIsExpanded(true)
                }}
                locationSearchApi={config?.endpoints?.locationSearch}
                locationFindApi={config?.endpoints?.locationFind}
                dictionary={dictionary}
              />
            </div>

            {dropOffAtDiffLocation && (
              <div className="col-lg-6">
                <SelectLocation
                  name={'dropOffLocation'}
                  label={dictionary.dropOffLabel}
                  control={control}
                  setValue={setValue}
                  watch={watch}
                  error={errors.dropOffLocation}
                  locationSearchApi={config?.endpoints?.locationSearch}
                  locationFindApi={config?.endpoints?.locationFind}
                  dictionary={dictionary}
                />
              </div>
            )}
          </div>
          <div className="row">
            <div className="col-lg-6 s-inline-widget__date-time">
              <DatePicker
                ref={pickUpDateRef}
                name="pickUpDate"
                label={dictionary.pickupDateLabel}
                control={control}
                setValue={setValue}
                disableBefore={new Date()}
                disableAfter={oneYearLater}
                error={errors.pickUpDate}
                dictionary={dictionary}
                defaultValue={currentValues?.pickUpDate}
                onChange={handlePickUpDateChange}
              />

              <SelectField
                ref={pickUpTimeRef}
                name={'pickUpTime'}
                control={control}
                label={dictionary.timeLabel}
                options={timeOptions?.map((option) => ({
                  value: option,
                  text: option,
                }))}
                error={errors.pickUpTime}
                isSelectTime
                onChange={() => dropOffDateRef.current?.open()}
              />
            </div>

            <div className="col-lg-6 s-inline-widget__date-time">
              <DatePicker
                ref={dropOffDateRef}
                name="dropOffDate"
                label={dictionary.dropOffDateLabel}
                control={control}
                setValue={setValue}
                disableBefore={
                  watch('pickUpDate')
                    ? new Date(watch('pickUpDate'))
                    : new Date()
                }
                disableAfter={OneYearAndMonthLater}
                error={errors.dropOffDate}
                dictionary={dictionary}
                defaultValue={currentValues?.dropOffDate}
                onChange={() => dropOffTimeRef.current?.open()}
              />
              <SelectField
                ref={dropOffTimeRef}
                name={'dropOffTime'}
                control={control}
                label={dictionary.timeLabel}
                options={
                  watch('pickUpTime') && isSameDay
                    ? timeOptions
                        ?.filter(
                          (_, index) =>
                            index > timeOptions.indexOf(watch('pickUpTime'))
                        )
                        ?.map((option) => ({
                          value: option,
                          text: option,
                        }))
                    : timeOptions?.map((option) => ({
                        value: option,
                        text: option,
                      }))
                }
                error={errors.dropOffTime}
              />
            </div>
          </div>
          <div className="row">
            <div className="col-lg-6">
              <TextField
                label={dictionary.aegeanCodeLabel}
                name="aegeanCode"
                control={control}
                error={errors.aegeanCode}
              />
            </div>
            <div className="col-lg-6">
              <TextField
                label={dictionary.userIdLabel}
                name="userId"
                control={control}
                error={errors.userId}
              />
            </div>
          </div>

          <div className="row">
            <div className="col-lg-3 col-6">
              <SelectField
                name="promoType"
                control={control}
                label={dictionary.promoCodeTypeLabel}
                options={promoCodeTypeOptions}
                error={errors.promoType}
              />
            </div>
            <div className="col-lg-3 col-6">
              <TextField
                label={dictionary.promoCodeLabel}
                name={`promoCode`}
                control={control}
                error={errors.promoCode}
              />
            </div>
            {/* <div className="col-lg-3">
              <SelectField
                name={'driverAge'}
                control={control}
                label={dictionary.driverAgeLabel}
                options={driverAgeOptions.map((option) => ({
                  value: option,
                  text: option,
                }))}
                error={errors.driverAge}
              />
            </div> */}
            <div className="col-lg-3">
              <button
                className="btn btn-primary"
                type="submit"
                disabled={loading}
                title={dictionary.findCarBtn}>
                {dictionary.findCarBtn}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}

// export default BookingWidget

export default ({ handleCancel, editMode = false }) => {
  const [data, setData] = useState()

  useEffect(() => {
    getData(window.bookingWidgetEndpoint).then((res) => setData(res.data))
  }, [])

  if (!data)
    return (
      <div className="d-flex justify-content-center py-4">
        <CircularProgress />
      </div>
    )

  return (
    <BookingWidget
      editMode={editMode}
      data={data}
      handleCancel={handleCancel}
    />
  )
}
